import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';

import { useCart, type CartLine } from '@/store/cart';
import { useCartImport } from '@/hooks/useCartImport';
import { useEventCatalog } from '@/hooks/useEventCatalog';
import { useInventory } from '@/hooks/useInventory';
import { getSeatMap } from '@/lib/api/taquilla';
import { lockCart, type LockCartResponse, CheckoutClientError } from '@/lib/checkoutClient';
import { messageForCode } from '@/lib/checkout/messages';
import { formatCOP } from '@/lib/format';
import { Badge, Button, Card, CardTitle, Modal, Skeleton, toast } from '@/components/ui';
import { PageBanner } from '@/components/ui/PageBanner';

/**
 * CartReview — Click 2 page.
 *
 * ROUND21:
 *
 *   - **#2 multi-event tickets** — el cart puede contener boletas de
 *     varios eventos a la vez. Renderizo una sección por eventId, cada una
 *     con su propio `useInventory` para enriquecer locality names + precios
 *     en sync con el SeatPicker.
 *
 *   - **#4 custom modal** — "Vaciar carrito" usa el componente Modal
 *     (Radix Dialog + a11y wiring) en lugar del `window.confirm` nativo.
 *
 *   - **"Solo boletas" guard** — el flow legacy `lockCart` opera por
 *     evento. Cuando el cart tiene boletas de >1 evento distinto, deshabilito
 *     el CTA "Solo boletas" con un helper text. El path canónico "Pagar Todo"
 *     (fn_create_unified_order de admin R30) acepta items de múltiples
 *     eventos en un solo payload.
 */
export function CartReview(): JSX.Element {
  const navigate = useNavigate();
  const cart = useCart();
  // OPERATOR #4 (2026-06-22) — Si llegamos desde el Checkout de Web con un
  // carrito en `?fc_cart=...`, lo importamos (resolviendo stageId contra el
  // inventario) y limpiamos el param. No-op si no viene el param.
  useCartImport();
  const [submitting, setSubmitting] = useState(false);
  const [clearOpen, setClearOpen] = useState(false);
  // Hoisted al top para respetar Rules of Hooks (no se llama dentro de
  // conditionales o post-return temprano).
  const [eventSubtotals, setEventSubtotals] = useState<Record<string, number>>({});

  const hasEventLines = cart.lines.length > 0;
  const hasProductLines = cart.productLines.length > 0;

  // ROUND21 #2 — agrupamos las líneas por eventId. Preservamos índices
  // originales (`originalIdx`) para que `removeLine(idx)` siga funcionando.
  type LineWithIdx = { line: CartLine; originalIdx: number };
  const groupsByEvent = new Map<string | 'unknown', LineWithIdx[]>();
  cart.lines.forEach((line, originalIdx) => {
    const key = line.eventId ?? 'unknown';
    const arr = groupsByEvent.get(key) ?? [];
    arr.push({ line, originalIdx });
    groupsByEvent.set(key, arr);
  });
  const eventIdsInCart = [...groupsByEvent.keys()].filter((k): k is string => k !== 'unknown');
  const hasMultipleEvents = eventIdsInCart.length > 1;

  const lockMutation = useMutation<LockCartResponse, CheckoutClientError>({
    mutationFn: async () => {
      if (!cart.eventId) throw new CheckoutClientError('INVALID_CART', 'Cart has no event', 400);
      if (cart.lines.length === 0)
        throw new CheckoutClientError('INVALID_CART', 'Cart is empty', 400);
      if (hasMultipleEvents) {
        // Defensa redundante con el guard del CTA: el path legacy no soporta
        // múltiples eventos. El usuario debería ver el CTA deshabilitado,
        // pero si igual llega acá, fallamos en cliente con mensaje claro.
        throw new CheckoutClientError(
          'INVALID_CART',
          'Solo boletas no soporta múltiples eventos. Usá Pagar Todo.',
          400,
        );
      }
      const idem = cart.beginCheckout();
      return lockCart({
        cartId: idem,
        items: cart.lines,
        idempotencyKey: idem,
      });
    },
    onSuccess: (data) => {
      cart.applyLock({ orderId: data.orderId, expiresAt: data.expiresAt });
      toast.success({
        title: 'Carrito reservado',
        description: 'Confirmá el pago para emitir tus boletas.',
      });
      navigate('/checkout', { replace: false });
    },
    onError: (err) => {
      const code = err.code;
      const status = err.status;
      toast.error({ title: 'No pudimos reservar', description: messageForCode(code, status) });
      if (
        code === 'INSUFFICIENT_INVENTORY' ||
        code === 'OUT_OF_STOCK' ||
        code === 'SEAT_ALREADY_LOCKED'
      ) {
        if (cart.eventId) navigate(`/event/${cart.eventId}`);
      } else if (code === 'CART_EXPIRED') {
        cart.reset();
        navigate('/');
      } else if (code === 'ORDER_INVALID_STATE' || code === 'INVALID_STATE') {
        cart.reset();
        if (cart.eventId) navigate(`/event/${cart.eventId}`);
        else navigate('/');
      } else if (code === 'UNAUTHORIZED') {
        navigate(`/login?from=/cart`);
      }
    },
    onSettled: () => setSubmitting(false),
  });

  /** F5 — todas las líneas por-silla deben tener titular antes de reservar. */
  function missingHolders(): boolean {
    return cart.lines.some((l) => l.seatIdentifier && !(l.holderName ?? '').trim());
  }

  function handleContinueEvents(): void {
    if (missingHolders()) {
      toast.error({
        title: 'Faltan datos del titular',
        description: 'Completá el nombre del titular de cada asiento antes de continuar.',
      });
      return;
    }
    setSubmitting(true);
    lockMutation.mutate();
  }

  function handleContinueProducts(): void {
    navigate('/checkout?mode=product');
  }

  // ROUND18 — "Pagar Todo" (unified RPC). ROUND21 #2: acepta items de
  // múltiples eventos sin cambios.
  // R36 C5 FIX — el RPC `fn_create_unified_order` espera `locality_id`
  // (event_stage_localities.id), NO `stage_inventory_id`. El comment
  // anterior estaba mal. El error "locality_not_found: <NULL>" venía
  // porque el RPC leía `v_item->>'locality_id'` → null → cast a uuid
  // → SELECT con id=NULL → no match → exception.
  type UnifiedOrderResult = {
    ok?: boolean;
    order_id?: string;
    status?: string;
    total_cents?: number;
    cash_remaining_cents?: number;
    [key: string]: unknown;
  };

  // GAP-06 (ver endpoint_gaps.md): no existe `fn_create_unified_order` ni un
  // endpoint de orden unificada multi-evento + productos. "Pagar Todo" reusa
  // el flujo por evento (cart → order → checkout) para el evento actual. El
  // multi-evento queda con el mensaje accionable de "pagá cada evento por
  // separado" (ya degradado en el código legacy). Los productos partner se
  // compran en su propio flujo.
  const unifiedMutation = useMutation<UnifiedOrderResult, Error>({
    mutationFn: async () => {
      const eventLines = cart.lines.filter((l) => l.eventId);
      if (eventLines.length === 0 && cart.productLines.length === 0) {
        throw new Error('empty_cart');
      }
      if (hasMultipleEvents) {
        throw new Error('multi_event_unsupported');
      }
      if (eventLines.length > 0) {
        const idem = cart.beginCheckout();
        const lock = await lockCart({
          cartId: idem,
          items: eventLines,
          idempotencyKey: idem,
        });
        cart.applyLock({ orderId: lock.orderId, expiresAt: lock.expiresAt });
        return { ok: true, order_id: lock.orderId };
      }
      // Sólo productos → flujo de productos.
      return { ok: true };
    },
    onSuccess: (result) => {
      if (typeof result.order_id === 'string' && result.order_id) {
        toast.success({
          title: 'Carrito reservado',
          description: 'Confirmá el pago para emitir tus boletas.',
        });
        navigate('/checkout');
        return;
      }
      // Sin boletas → checkout de productos.
      navigate('/checkout?mode=product');
    },
    onError: (err) => {
      const msg = err instanceof Error ? err.message : '';
      let description = 'Probá de nuevo en unos segundos.';
      if (msg.includes('empty_cart')) {
        description = 'Tu carrito está vacío.';
      } else if (msg.includes('multi_event_unsupported')) {
        description =
          'El "Pagar Todo" multi-evento está temporalmente en mantenimiento. Por favor pagá cada evento por separado desde su página de detalle.';
      } else if (err instanceof CheckoutClientError) {
        description = messageForCode(err.code, err.status);
      }
      toast.error({ title: 'No pudimos crear la orden', description });
    },
  });

  function handlePayAll(): void {
    if (missingHolders()) {
      toast.error({
        title: 'Faltan datos del titular',
        description: 'Completá el nombre del titular de cada asiento antes de continuar.',
      });
      return;
    }
    unifiedMutation.mutate();
  }

  function confirmClearCart(): void {
    cart.clearCart();
    setClearOpen(false);
    toast.success({ title: 'Carrito vacío', description: 'Eliminamos todos los items.' });
  }

  // ---- Empty cart ----
  if (!hasEventLines && !hasProductLines) {
    return (
      <section className="container-fc py-12">
        <h1 className="font-display text-3xl">Tu carrito</h1>
        <p className="mt-2 text-fc-muted">No tenés items en el carrito todavía.</p>
        <div className="mt-6 flex flex-col gap-2 md:flex-row">
          <Button variant="primary" onClick={() => navigate('/')}>
            Ver eventos
          </Button>
          <Button variant="secondary" onClick={() => navigate('/store')}>
            Explorar tienda
          </Button>
        </div>
      </section>
    );
  }

  // ---- Product lines subtotal (event totals se calculan por sección) ----
  const productTotal = cart.productLines.reduce((sum, p) => sum + p.unitPriceCents * p.quantity, 0);

  // Sumamos los subtotales por evento — cada sección reporta su total al
  // padre via callback `onSubtotal` (`setEventSubtotals` hoisted al top).
  const eventTotal = Object.values(eventSubtotals).reduce((s, n) => s + n, 0);
  const combinedTotal = eventTotal + productTotal;

  return (
    <>
      <PageBanner
        eyebrow="Compra"
        title="Tu Carrito"
        subtitle="Revisá tu selección antes de pagar."
      />
      <section className="container-fc py-12 md:py-16">
        <header className="mb-6 flex flex-col items-start gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
          <div>
            {eventIdsInCart.length === 1 && cart.eventId && (
              <p className="mt-1 text-fc-muted">
                <button
                  type="button"
                  onClick={() => navigate(`/event/${cart.eventId}`)}
                  className="underline hover:text-fc-ink"
                >
                  Volver al evento
                </button>
              </p>
            )}
            {hasMultipleEvents && (
              <p className="mt-1 text-sm text-fc-muted">
                Boletas de {eventIdsInCart.length} eventos distintos en una sola orden.
              </p>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {/* ROUND21 #4 — Vaciar carrito con Modal custom (no window.confirm). */}
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setClearOpen(true)}
              aria-label="Vaciar carrito"
              disabled={unifiedMutation.isPending || submitting || lockMutation.isPending}
            >
              Vaciar carrito
            </Button>
          </div>
        </header>

        <div className="flex flex-col gap-6">
          {/* ROUND21 #2 — una sección de Boletas por eventId. */}
          {eventIdsInCart.map((eventId) => (
            <EventTicketsSection
              key={eventId}
              eventId={eventId}
              lines={groupsByEvent.get(eventId) ?? []}
              onRemoveLine={(originalIdx) => cart.removeLine(originalIdx)}
              onSubtotal={(subtotal) =>
                setEventSubtotals((prev) =>
                  prev[eventId] === subtotal ? prev : { ...prev, [eventId]: subtotal },
                )
              }
            />
          ))}

          {/* Líneas legacy sin eventId (carts pre-R21 migrados). */}
          {groupsByEvent.has('unknown') && (
            <Card variant="outlined" padded>
              <CardTitle>Boletas (sin evento)</CardTitle>
              <ul className="mt-3 divide-y divide-fc-line">
                {(groupsByEvent.get('unknown') ?? []).map(({ line, originalIdx }) => (
                  <li
                    key={`legacy-${originalIdx}`}
                    className="flex items-center justify-between py-3"
                  >
                    <span className="text-fc-ink">
                      Cantidad {line.quantity} · {formatCOP(null)}
                    </span>
                    <Button variant="ghost" size="sm" onClick={() => cart.removeLine(originalIdx)}>
                      Quitar
                    </Button>
                  </li>
                ))}
              </ul>
            </Card>
          )}

          {hasProductLines && (
            <Card variant="default" padded>
              <CardTitle>Productos partners</CardTitle>
              <ul className="mt-4 divide-y divide-fc-line">
                {cart.productLines.map((p) => (
                  <li
                    key={p.productId}
                    className="flex flex-col gap-2 py-3 md:flex-row md:items-center md:justify-between"
                  >
                    <div className="min-w-0">
                      <div className="font-medium text-fc-ink">{p.title}</div>
                      <div className="text-sm text-fc-muted">
                        {p.partnerName} · Cantidad {p.quantity} · {formatCOP(p.unitPriceCents)} c/u
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-base font-semibold tabular-nums">
                        {formatCOP(p.unitPriceCents * p.quantity)}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={`Quitar ${p.title}`}
                        onClick={() => cart.removeProductLine(p.productId)}
                      >
                        Quitar
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
              <div className="mt-4 flex items-center justify-between border-t border-fc-line pt-3 text-sm">
                <span className="text-fc-muted">Subtotal productos</span>
                <span className="font-semibold tabular-nums">{formatCOP(productTotal)}</span>
              </div>
            </Card>
          )}

          <Card variant="ghost" padded>
            <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
              <div>
                <div className="text-sm text-fc-muted">Total combinado</div>
                <div className="text-2xl font-semibold tabular-nums">
                  {formatCOP(combinedTotal)}
                </div>
                {hasEventLines && hasProductLines && (
                  <p className="mt-1 text-xs text-fc-muted">
                    Boletas + productos en una sola orden.
                  </p>
                )}
              </div>
              <div className="flex flex-col items-stretch gap-2 md:items-end">
                <Button
                  variant="primary"
                  size="lg"
                  onClick={handlePayAll}
                  disabled={unifiedMutation.isPending || submitting || lockMutation.isPending}
                >
                  {unifiedMutation.isPending ? 'Procesando…' : 'Pagar Todo'}
                </Button>
                {(hasEventLines || hasProductLines) && (
                  <div className="flex flex-wrap gap-2">
                    {hasEventLines && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleContinueEvents}
                        disabled={
                          submitting ||
                          lockMutation.isPending ||
                          unifiedMutation.isPending ||
                          hasMultipleEvents
                        }
                        title={hasMultipleEvents ? 'Multi-evento: usá Pagar Todo' : undefined}
                      >
                        {submitting || lockMutation.isPending ? 'Reservando…' : 'Solo boletas'}
                      </Button>
                    )}
                    {hasProductLines && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleContinueProducts}
                        disabled={unifiedMutation.isPending}
                      >
                        Solo productos
                      </Button>
                    )}
                  </div>
                )}
                {hasMultipleEvents && (
                  <p className="text-xs text-fc-muted md:text-right">
                    &ldquo;Solo boletas&rdquo; sólo aplica a un evento por vez. Usá Pagar Todo para
                    incluir boletas de varios eventos.
                  </p>
                )}
              </div>
            </div>
          </Card>
        </div>

        {/* ROUND21 #4 — Modal custom para confirmar Vaciar carrito. */}
        <Modal
          open={clearOpen}
          onOpenChange={setClearOpen}
          title="¿Vaciar el carrito?"
          description="Vas a eliminar todas las boletas y productos de tu carrito. Esta acción no se puede deshacer."
          footer={
            <>
              <Button variant="ghost" onClick={() => setClearOpen(false)}>
                Cancelar
              </Button>
              <Button variant="primary" onClick={confirmClearCart}>
                Sí, vaciar
              </Button>
            </>
          }
        >
          <p className="text-sm text-fc-muted">
            Total de items a eliminar:{' '}
            <span className="font-semibold tabular-nums text-fc-ink">
              {cart.totalQuantity() + cart.totalProductQuantity()}
            </span>
          </p>
        </Modal>
      </section>
    </>
  );
}

/**
 * Sección de boletas de UN evento. Encapsula el `useEventCatalog` +
 * `useInventory` para que se rendericen por separado y no se mezclen los
 * nombres / precios entre eventos distintos.
 */
function EventTicketsSection({
  eventId,
  lines,
  onRemoveLine,
  onSubtotal,
}: {
  eventId: string;
  lines: { line: CartLine; originalIdx: number }[];
  onRemoveLine: (originalIdx: number) => void;
  onSubtotal: (subtotal: number) => void;
}): JSX.Element {
  const catalog = useEventCatalog(eventId);
  const inventory = useInventory(eventId);
  const cart = useCart();
  // F5 — mapa de asientos para resolver nombre + precio + label de las líneas
  // por-silla (cuya localityId es la localidad real, no la etapa del inventory).
  const seatMapQ = useQuery({
    queryKey: ['seat-map', eventId],
    queryFn: () => getSeatMap(eventId),
    enabled: !!eventId,
    staleTime: 30_000,
  });

  if (catalog.isLoading || inventory.isLoading) {
    return (
      <Card variant="default" padded>
        <CardTitle>Boletas</CardTitle>
        <div className="mt-3 flex flex-col gap-2">
          <Skeleton shape="h-12 w-full" />
          <Skeleton shape="h-12 w-full" />
        </div>
      </Card>
    );
  }

  const inventoryLocalityMap = new Map<string, { name: string; priceFullCents: number | null }>();
  for (const stage of inventory.data?.stages ?? []) {
    for (const loc of stage.localities) {
      inventoryLocalityMap.set(loc.localityId, {
        name: loc.name,
        priceFullCents: loc.priceFullCents ?? null,
      });
    }
  }
  const catalogLocalityMap = new Map(
    (catalog.data?.localities ?? []).map((l) => [l.id, l] as const),
  );

  // F5 — mapas del seat-map: localidad → {name, price}, y seat → {locName, label}.
  const seatMapLocMap = new Map<string, { name: string; priceCop: number | null }>();
  const seatMapSeatMap = new Map<string, { localityName: string; label: string | null }>();
  for (const loc of seatMapQ.data?.localities ?? []) {
    seatMapLocMap.set(loc.id, { name: loc.name, priceCop: loc.price_cop ?? null });
    for (const s of loc.seats) {
      // F5 iter2 — etiqueta "Fila A · 12" si hay nomenclatura; si no, el label compacto.
      const lbl = s.row && s.number ? `Fila ${s.row} · ${s.number}` : (s.label ?? null);
      seatMapSeatMap.set(s.id, { localityName: loc.name, label: lbl });
    }
  }

  const lineDetails = lines.map(({ line, originalIdx }) => {
    // Línea POR SILLA (F5): la localityId es la localidad real; nombre/precio/label
    // salen del seat-map. La etiqueta agrega el asiento ("VIP 4 · A-12").
    if (line.seatIdentifier) {
      const loc = line.localityId ? seatMapLocMap.get(line.localityId) : undefined;
      const seat = seatMapSeatMap.get(line.seatIdentifier);
      const baseName = loc?.name ?? seat?.localityName ?? 'Asiento';
      const localityName = seat?.label ? `${baseName} · ${seat.label}` : baseName;
      const unitPriceCents = loc?.priceCop ?? null;
      return {
        originalIdx,
        line,
        localityName,
        unitPriceCents,
        lineTotalCents: unitPriceCents !== null ? unitPriceCents * line.quantity : null,
        isSeat: true,
      };
    }
    const invLoc = line.localityId ? inventoryLocalityMap.get(line.localityId) : undefined;
    const catLoc = line.localityId ? catalogLocalityMap.get(line.localityId) : undefined;
    const localityName = invLoc?.name ?? catLoc?.name ?? 'Localidad';
    const unitPriceCents = invLoc?.priceFullCents ?? catLoc?.priceFullCents ?? null;
    return {
      originalIdx,
      line,
      localityName,
      unitPriceCents,
      lineTotalCents: unitPriceCents !== null ? unitPriceCents * line.quantity : null,
      isSeat: false,
    };
  });

  const subtotal = lineDetails.reduce(
    (sum, d) => (d.lineTotalCents !== null ? sum + d.lineTotalCents : sum),
    0,
  );
  const hasPriceGaps = lineDetails.some((d) => d.unitPriceCents === null);
  const eventTitle = catalog.data?.event.title ?? 'Boletas del evento';

  // Reportamos el subtotal al padre para que sumarice el total combinado.
  // Defer al efecto siguiente para evitar setState durante render del padre.
  if (subtotal !== undefined) {
    // Llamamos sincrónicamente; el padre usa el guard "si igual no
    // re-renderea" para evitar loops.
    queueMicrotask(() => onSubtotal(subtotal));
  }

  return (
    <Card variant="default" padded>
      <div className="flex items-baseline justify-between gap-3">
        <CardTitle>{eventTitle}</CardTitle>
        <Badge tone="info">Boletas</Badge>
      </div>
      <ul className="mt-4 divide-y divide-fc-line">
        {lineDetails.map(
          ({ originalIdx, line, localityName, unitPriceCents, lineTotalCents, isSeat }) => (
            <li
              key={`${line.stageId}-${line.localityId}-${line.seatIdentifier ?? ''}-${originalIdx}`}
              className="flex flex-col gap-2 py-3"
            >
              <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
                <div className="min-w-0">
                  <div className="font-medium text-fc-ink">{localityName}</div>
                  <div className="text-sm text-fc-muted">
                    {isSeat ? '1 asiento' : `Cantidad ${line.quantity}`} ·{' '}
                    {formatCOP(unitPriceCents)} c/u
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-base font-semibold tabular-nums">
                    {formatCOP(lineTotalCents)}
                  </span>
                  <Button
                    variant="ghost"
                    size="sm"
                    aria-label={`Quitar ${localityName}`}
                    onClick={() => onRemoveLine(originalIdx)}
                  >
                    Quitar
                  </Button>
                </div>
              </div>
              {/* F5 — titular del asiento (QR nominal). Requerido por el Operator:
                "ponerle todo al detalle antes de confirmar". */}
              {isSeat && (
                <label className="flex flex-col gap-1">
                  <span className="text-xs font-medium uppercase tracking-wide text-fc-muted">
                    Titular de la boleta
                  </span>
                  <input
                    type="text"
                    value={line.holderName ?? ''}
                    onChange={(e) => cart.updateLineHolder(originalIdx, e.target.value)}
                    placeholder="Nombre y apellido del asistente"
                    className="bg-fc-surface rounded-card border border-fc-line px-3 py-2 text-sm text-fc-ink placeholder:text-fc-muted focus:border-fc-ink focus:outline-none"
                  />
                </label>
              )}
            </li>
          ),
        )}
      </ul>
      <div className="mt-4 flex items-center justify-between border-t border-fc-line pt-3 text-sm">
        <span className="text-fc-muted">Subtotal boletas</span>
        <span className="font-semibold tabular-nums">{formatCOP(subtotal)}</span>
      </div>
      {hasPriceGaps && (
        <Badge tone="warning" className="mt-2">
          Algunos precios se confirman al avanzar.
        </Badge>
      )}
    </Card>
  );
}
